import type { AutomationBlock, AutomationBlockItem, AutomationBlockType } from '../types';

/**
 * Modelo do editor de automações (Fase 4 / A3): tipos de bloco, catálogo de
 * itens com os valores iniciais, portas de saída e resumo de cada bloco.
 * O formato é o do export do DataCrazy — o que o editor grava, o motor roda.
 */

export const BLOCK_META: Record<
  AutomationBlockType,
  { label: string; color: string; hint: string }
> = {
  trigger: { label: 'Gatilho', color: '#16A34A', hint: 'O que inicia a automação' },
  action: { label: 'Ações', color: '#2563EB', hint: 'Lead, negócio, tags, produto, atendente' },
  condition: { label: 'Condição', color: '#D97706', hint: 'Desvia em verdadeiro ou falso' },
  'field-operation': {
    label: 'Operação de campo',
    color: '#7C3AED',
    hint: 'Preenche campos e trata telefone',
  },
  delay: { label: 'Espera', color: '#64748B', hint: 'Pausa em segundos, horas ou dias' },
  chat: { label: 'Mensagens', color: '#0891B2', hint: 'Registradas — o envio está desligado' },
  randomizer: { label: 'Randomizador', color: '#DB2777', hint: 'Divide em caminhos por %' },
  api: { label: 'Requisição HTTP', color: '#0F766E', hint: 'Chama uma API externa' },
};

export const BLOCK_ORDER: AutomationBlockType[] = [
  'trigger',
  'action',
  'condition',
  'field-operation',
  'delay',
  'chat',
  'randomizer',
  'api',
];

export type ItemsKey = 'triggers' | 'actions' | 'conditions' | 'fieldOperations' | 'messages';

export const ITEMS_KEY: Partial<Record<AutomationBlockType, ItemsKey>> = {
  trigger: 'triggers',
  action: 'actions',
  condition: 'conditions',
  'field-operation': 'fieldOperations',
  chat: 'messages',
};

export interface CatalogItem {
  name: string;
  label: string;
  defaults: Record<string, unknown>;
  /** Só aparece quando veio de importação (não se adiciona pelo editor). */
  legacy?: boolean;
}

export const ITEM_CATALOG: Record<ItemsKey, CatalogItem[]> = {
  triggers: [
    {
      name: 'json-http-request-trigger',
      label: 'Requisição HTTP (webhook)',
      defaults: { datasourceName: 'Api-request-1' },
    },
    {
      name: 'message-received-trigger',
      label: 'Mensagem recebida',
      defaults: {
        type: 'contains',
        keywords: [],
        instanceId: '',
        listenGroup: false,
        receiveJson: false,
        initializeSession: 'always',
      },
    },
    { name: 'business-created-trigger', label: 'Negócio criado', defaults: { pipelineId: '' } },
    {
      name: 'business-stage-entered-trigger',
      label: 'Negócio entrou na etapa',
      defaults: { stageId: '' },
    },
    { name: 'business-won-trigger', label: 'Negócio ganho', defaults: { pipelineId: '' } },
    { name: 'business-lost-trigger', label: 'Negócio perdido', defaults: { pipelineId: '' } },
    {
      name: 'instagram-comment-received-trigger',
      label: 'Comentário no Instagram',
      defaults: {
        type: 'contains',
        keywords: [],
        instanceId: '',
        filterMedia: false,
        mediaIds: [],
        receiveJson: false,
        initializeSession: 'always',
      },
    },
  ],
  actions: [
    { name: 'create-lead-action', label: 'Criar lead', defaults: {} },
    { name: 'create-business-action', label: 'Criar negócio', defaults: { stageId: '' } },
    { name: 'move-business-action', label: 'Mover negócio', defaults: { stageId: '' } },
    { name: 'win-business-action', label: 'Ganhar negócio', defaults: {} },
    {
      name: 'add-attendant-on-business-action',
      label: 'Definir atendente',
      defaults: { attendantId: '', addSameAttendantToLead: true },
    },
    {
      name: 'distribute-attendant-action',
      label: 'Distribuir para vendedor disponível',
      defaults: {
        attendantsIds: [],
        maxPending: 2,
        slaMinutes: 25,
        addSameAttendantToLead: true,
      },
    },
    {
      name: 'add-product-on-business-action',
      label: 'Adicionar produto',
      defaults: { productId: '', productSku: '', price: '', quantity: '' },
    },
    { name: 'add-tag-action', label: 'Adicionar tag', defaults: { tagIds: [], tagName: '' } },
    {
      name: 'create-tag-action',
      label: 'Criar tag',
      defaults: { name: '', color: '#94A3B8', description: '' },
    },
  ],
  conditions: [
    { name: 'lead-exists-condition', label: 'Lead existe', defaults: {} },
    {
      name: 'lead-with-phone-exists-condition',
      label: 'Existe lead com o telefone',
      defaults: { phone: '' },
    },
    {
      name: 'lead-has-business-on-pipeline-condition',
      label: 'Lead tem negócio na pipeline',
      defaults: { pipelineId: '' },
    },
    {
      name: 'lead-has-business-on-stage-condition',
      label: 'Lead tem negócio na etapa',
      defaults: { stageId: '' },
    },
    { name: 'instagram-follower', label: 'Segue no Instagram', defaults: {} },
  ],
  fieldOperations: [
    {
      name: 'set-field-operation',
      label: 'Definir campo',
      defaults: { parameter: 'leadName', value: '' },
    },
    {
      name: 'parse-phone-field-operation',
      label: 'Tratar telefone',
      defaults: { phone: '', datasourceName: 'phone-1', defaultCountry: 'BR' },
    },
  ],
  messages: [
    {
      name: 'send-text-message',
      label: 'Texto',
      defaults: {
        text: '',
        buttons: [],
        breakMessages: false,
        breakMessagesIntervalInSeconds: 2,
      },
    },
    { name: 'delay-message', label: 'Pausa entre mensagens', defaults: { seconds: 5 } },
    {
      name: 'send-comment-instagram',
      label: 'Responder comentário no Instagram',
      defaults: { text: '', privateReply: false },
    },
  ],
};

const LABELS = new Map(
  Object.values(ITEM_CATALOG)
    .flat()
    .map((item) => [item.name, item.label]),
);

export const itemLabel = (name: string): string => LABELS.get(name) ?? name;

export function itemsOf(block: AutomationBlock): AutomationBlockItem[] {
  const key = ITEMS_KEY[block.type];
  const list = key ? block.options?.[key] : undefined;
  return Array.isArray(list) ? (list as AutomationBlockItem[]) : [];
}

export const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export function newItem(name: string): AutomationBlockItem {
  const entry = Object.values(ITEM_CATALOG)
    .flat()
    .find((item) => item.name === name);
  return {
    name,
    stepId: newId(),
    options: JSON.parse(JSON.stringify(entry?.defaults ?? {})) as Record<string, unknown>,
  };
}

/** Bloco novo, com as ligações vazias do tipo. */
export function newBlock(type: AutomationBlockType, x: number, y: number): AutomationBlock {
  const base = { id: newId(), type, presentation: { x: Math.round(x), y: Math.round(y) } };
  switch (type) {
    case 'trigger':
      return { ...base, options: { triggers: [], nextBlockId: '' } };
    case 'action':
      return { ...base, options: { actions: [], nextBlockId: '', errorNextBlockId: '' } };
    case 'condition':
      return {
        ...base,
        options: { conditions: [], trueNextBlockId: '', falseNextBlockId: '' },
      };
    case 'field-operation':
      return {
        ...base,
        options: { fieldOperations: [], nextBlockId: '', errorNextBlockId: '' },
      };
    case 'delay':
      return {
        ...base,
        options: {
          delay: { name: 'seconds-delay', options: { seconds: 60 } },
          nextBlockId: '',
          refreshLeadDataOnResume: true,
        },
      };
    case 'chat':
      return { ...base, options: { messages: [], nextBlockId: '', errorNextBlockId: '' } };
    case 'randomizer':
      return {
        ...base,
        options: {
          randomizers: [
            { id: newId(), name: 'A', perc: 50, nextBlockId: '' },
            { id: newId(), name: 'B', perc: 50, nextBlockId: '' },
          ],
        },
      };
    case 'api':
      return {
        ...base,
        options: {
          method: 'POST',
          url: '',
          headers: [],
          body: '',
          datasourceName: 'api-1',
          nextBlockId: '',
          errorNextBlockId: '',
        },
      };
  }
}

export interface Port {
  key: string;
  label: string;
  color: string;
}

/** Saídas do bloco, na ordem em que aparecem (de cima para baixo). */
export function portsOf(block: AutomationBlock): Port[] {
  switch (block.type) {
    case 'condition':
      return [
        { key: 'trueNextBlockId', label: 'Verdadeiro', color: '#16A34A' },
        { key: 'falseNextBlockId', label: 'Falso', color: '#DC2626' },
      ];
    case 'randomizer':
      return (
        (block.options?.randomizers ?? []) as Array<{ name?: string; perc?: number }>
      ).map((path, index) => ({
        key: `randomizers.${index}`,
        label: `${path.name ?? index + 1} · ${path.perc ?? 0}%`,
        color: '#DB2777',
      }));
    case 'trigger':
    case 'delay':
      return [{ key: 'nextBlockId', label: 'Próximo', color: '#475569' }];
    case 'chat':
      return [
        { key: 'nextBlockId', label: 'Próximo', color: '#475569' },
        { key: 'errorNextBlockId', label: 'Se der erro', color: '#DC2626' },
        ...replyButtonsOf(block).map(({ key, title }) => ({
          key,
          label: `Botão · ${title || 'sem texto'}`,
          color: '#7C3AED',
        })),
      ];
    default:
      return [
        { key: 'nextBlockId', label: 'Próximo', color: '#475569' },
        { key: 'errorNextBlockId', label: 'Se der erro', color: '#DC2626' },
      ];
  }
}

interface ChatButton {
  id?: string;
  text?: string;
  type?: string;
  url?: string;
  nextBlockId?: string;
}

/** Botões de resposta das mensagens do bloco (cada um é uma saída: `buttons.<msg>.<botão>`). */
export function replyButtonsOf(block: AutomationBlock): Array<{ key: string; title: string }> {
  if (block.type !== 'chat') return [];
  return itemsOf(block).flatMap((item, messageIndex) =>
    ((item.options?.buttons ?? []) as ChatButton[]).flatMap((button, buttonIndex) =>
      button?.type === 'link'
        ? []
        : [{ key: `buttons.${messageIndex}.${buttonIndex}`, title: button?.text ?? '' }],
    ),
  );
}

export function getLink(block: AutomationBlock, key: string): string {
  if (key.startsWith('buttons.')) {
    const [, messageIndex, buttonIndex] = key.split('.').map(Number);
    const button = (
      itemsOf(block)[messageIndex]?.options?.buttons as ChatButton[] | undefined
    )?.[buttonIndex];
    return button?.nextBlockId ?? '';
  }
  if (key.startsWith('randomizers.')) {
    const index = Number(key.split('.')[1]);
    const paths = (block.options?.randomizers ?? []) as Array<{ nextBlockId?: string }>;
    return paths[index]?.nextBlockId ?? '';
  }
  const value = block.options?.[key];
  return typeof value === 'string' ? value : '';
}

export function setLink(block: AutomationBlock, key: string, target: string): AutomationBlock {
  if (key.startsWith('buttons.')) {
    const [, messageIndex, buttonIndex] = key.split('.').map(Number);
    const messages = itemsOf(block).map((item, index) => {
      if (index !== messageIndex) return item;
      const buttons = [...((item.options?.buttons ?? []) as ChatButton[])];
      buttons[buttonIndex] = { ...buttons[buttonIndex], nextBlockId: target };
      return { ...item, options: { ...item.options, buttons } };
    });
    return { ...block, options: { ...block.options, messages } };
  }
  if (key.startsWith('randomizers.')) {
    const index = Number(key.split('.')[1]);
    const paths = [...((block.options?.randomizers ?? []) as Array<Record<string, unknown>>)];
    paths[index] = { ...paths[index], nextBlockId: target };
    return { ...block, options: { ...block.options, randomizers: paths } };
  }
  return { ...block, options: { ...block.options, [key]: target } };
}

/** Remove todas as ligações que apontam para `targetId` (ao excluir um bloco). */
export function unlinkTarget(blocks: AutomationBlock[], targetId: string): AutomationBlock[] {
  return blocks.map((block) => {
    let next = block;
    for (const port of portsOf(block)) {
      if (getLink(next, port.key) === targetId) next = setLink(next, port.key, '');
    }
    return next;
  });
}

const DELAY_UNITS: Record<string, [string, string]> = {
  'seconds-delay': ['seconds', 's'],
  'hours-delay': ['hours', 'h'],
  'days-delay': ['days', 'dia(s)'],
};

export function delayOf(block: AutomationBlock): { unit: string; amount: number } {
  const delay = (block.options?.delay ?? {}) as {
    name?: string;
    options?: Record<string, unknown>;
  };
  const unit = delay.name && DELAY_UNITS[delay.name] ? delay.name : 'seconds-delay';
  return { unit, amount: Number(delay.options?.[DELAY_UNITS[unit][0]] ?? 0) };
}

export function setDelay(
  block: AutomationBlock,
  unit: string,
  amount: number,
): AutomationBlock {
  const [field] = DELAY_UNITS[unit] ?? DELAY_UNITS['seconds-delay'];
  return {
    ...block,
    options: { ...block.options, delay: { name: unit, options: { [field]: amount } } },
  };
}

/** Linhas curtas que o cartão do bloco mostra no canvas. */
export function summaryOf(block: AutomationBlock): string[] {
  const options = block.options ?? {};
  switch (block.type) {
    case 'delay': {
      const { unit, amount } = delayOf(block);
      return [`Esperar ${amount} ${DELAY_UNITS[unit][1]}`];
    }
    case 'randomizer':
      return [`${((options.randomizers as unknown[]) ?? []).length} caminhos`];
    case 'api':
      return [`${String(options.method ?? 'GET')} ${String(options.url || '(sem URL)')}`];
    default: {
      const items = itemsOf(block);
      if (!items.length) return ['Sem itens — clique para configurar'];
      return items.map((item) => {
        const value = item.options ?? {};
        if (item.name === 'send-text-message')
          return `💬 ${String(value.text ?? '').slice(0, 40)}`;
        if (item.name === 'set-field-operation')
          return `${String(value.parameter)} ← ${String(value.value ?? '').slice(0, 30)}`;
        return itemLabel(item.name);
      });
    }
  }
}

/** Fontes de dados que o fluxo cria (para o menu "Inserir variável"). */
export function datasourcesOf(blocks: AutomationBlock[]): string[] {
  const names = new Set<string>();
  for (const block of blocks) {
    if (block.type === 'api' && block.options?.datasourceName) {
      names.add(String(block.options.datasourceName));
    }
    for (const item of itemsOf(block)) {
      const name = item.options?.datasourceName;
      if (typeof name === 'string' && name) names.add(name);
    }
    if (
      block.type === 'trigger' &&
      itemsOf(block).some((i) => i.name === 'message-received-trigger')
    ) {
      names.add('message');
    }
  }
  return [...names];
}

export const LEAD_VARIABLES: Array<[string, string]> = [
  ['leadName', 'Nome do lead'],
  ['leadFirstName', 'Primeiro nome do lead'],
  ['leadLastName', 'Último nome do lead'],
  ['leadPhone', 'Telefone do lead'],
  ['leadEmail', 'E-mail do lead'],
  ['leadSource', 'Origem do lead'],
];
